import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, PageHeader } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { PageWrapper } from '../components'
import { CheckOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Divider, InputNumber, Radio } from 'antd'
import styled from 'styled-components'
import ExternalLink from 'components/icons/ExternalLink'
import LockIcon from '../RotaBuilder/common/components/icons/LockIcon'
import { Text } from '@lmc/ui-kit/src/general'
import EnableGpsModal from './components/EnableGpsModal'
import { showToast } from 'common/utils'
import { postJSON } from 'common/services/api'
import * as GlobalActions from 'actions/Global'
import useUpdateGeofencingSettings from '../hooks/useUpdateGeofencingSettings'
import { GeofencingMode } from '@lmc/ts-common'

const GPS_SUPPORT_ARTICLE_URL =
  'https://support.logmycare.co.uk/en/articles/6418064-why-can-t-i-see-gps-locations-on-some-logs'

const MIN_DISTANCE_METRES = 30
const DEFAULT_DISTANCE_METRES = 100

const GeofencingSettingsScreen = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const home = useSelector(
    (state: { global: { home: Record<string, unknown> } }) => state.global.home
  )
  const [enableGpsModalOpen, setEnableGpsModalOpen] = useState(false)
  const [isEnablingGps, setIsEnablingGps] = useState(false)
  const { updateGeofencingSettings, isLoading: isUpdating } =
    useUpdateGeofencingSettings()

  const gpsEnabled =
    (home as any)?.geoLocation === true ||
    (home?.features as any)?.geoLocation?.enabled === true

  const rotaSettings = home?.rotaSettings as any
  const initialMode: GeofencingMode =
    rotaSettings?.geofencing === true
      ? ((rotaSettings?.geofencingMode as GeofencingMode) ?? GeofencingMode.Off)
      : GeofencingMode.Off
  const initialMaxDistance =
    rotaSettings?.geofencingMaxRadius != null
      ? Number(rotaSettings.geofencingMaxRadius)
      : DEFAULT_DISTANCE_METRES

  const [mode, setMode] = useState<GeofencingMode>(initialMode)
  const [maxDistanceMeters, setMaxDistanceMeters] =
    useState<number>(initialMaxDistance)

  const isDirty =
    mode !== initialMode ||
    (mode !== GeofencingMode.Off && maxDistanceMeters !== initialMaxDistance)

  const distanceError =
    mode !== GeofencingMode.Off && maxDistanceMeters < MIN_DISTANCE_METRES

  const handleEnableGpsConfirm = async () => {
    if (!home?.id) return
    setIsEnablingGps(true)
    try {
      const res = await postJSON({
        url: `list/homes/${home.id}`,
        json: { id: home.id, geoLocation: true },
        method: 'PUT'
      })
      dispatch(
        GlobalActions.setHomeDetails(
          (res as { result?: unknown }).result ?? res
        )
      )
      showToast({ title: t('SETTINGS_UPDATED') })
      setEnableGpsModalOpen(false)
    } catch {
      showToast({ title: t('SETTINGS_ERROR'), type: 'error' })
    } finally {
      setIsEnablingGps(false)
    }
  }

  const handleSave = async () => {
    if (!home?.id || !isDirty) return
    const isEnabled = mode !== GeofencingMode.Off
    await updateGeofencingSettings({
      geofencing: isEnabled,
      geofencingMode: mode,
      ...(isEnabled && { geofencingMaxRadius: maxDistanceMeters })
    })
  }

  return (
    <>
      <PageHeader
        title={t('GEOFENCING')}
        subtitle={t('GEOFENCING_INFO')}
        sticky
      />
      <PageWrapper>
        {!gpsEnabled ? (
          <ContentWrapper>
            <Card style={{ width: '100%' }}>
              <BlockContent>
                <LockIconWrapper>
                  <LockIcon />
                </LockIconWrapper>
                <MessageText>
                  {t('GEOFENCING_GPS_REQUIRED_MESSAGE')}
                </MessageText>
                <ActionsWrapper>
                  <ReadMoreLink
                    href={GPS_SUPPORT_ARTICLE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('READ_MORE')} <ExternalLink />
                  </ReadMoreLink>
                  <Button
                    variant="primary"
                    onClick={() => setEnableGpsModalOpen(true)}
                  >
                    {t('GEOFENCING_ENABLE_GPS')}
                  </Button>
                </ActionsWrapper>
              </BlockContent>
            </Card>
          </ContentWrapper>
        ) : (
          <>
            <ContentCard>
              <SectionTitle>{t('GEOFENCING_CHOOSE_STRICTLY')}</SectionTitle>
              <Radio.Group
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                style={{ display: 'contents' }}
              >
                <OptionsRow>
                  <OptionCard $selected={mode === GeofencingMode.Off}>
                    <RadioWrapper>
                      <Radio value={GeofencingMode.Off}>
                        <OptionLabel>{t('GEOFENCING_OFF')}</OptionLabel>
                      </Radio>
                      <OptionDesc level={2}>
                        {t('GEOFENCING_OFF_DESC')}
                      </OptionDesc>
                    </RadioWrapper>
                    <NestedBox>
                      <Text level={2}>{t('GEOFENCING_OFF_INFO')}</Text>
                    </NestedBox>
                  </OptionCard>

                  <OptionCard $selected={mode === GeofencingMode.WarningsOnly}>
                    <RadioWrapper>
                      <Radio value={GeofencingMode.WarningsOnly}>
                        <OptionLabel>{t('GEOFENCING_WARNINGS')}</OptionLabel>
                      </Radio>
                      <OptionDesc level={2}>
                        {t('GEOFENCING_WARNINGS_DESC')}
                      </OptionDesc>
                    </RadioWrapper>
                    <NestedBox>
                      <Text level={2}>
                        {t('GEOFENCING_WARNINGS_RECOMMENDED')}
                      </Text>
                    </NestedBox>
                  </OptionCard>

                  <OptionCard $selected={mode === GeofencingMode.CompleteBlock}>
                    <RadioWrapper>
                      <Radio value={GeofencingMode.CompleteBlock}>
                        <OptionLabel>{t('GEOFENCING_BLOCK')}</OptionLabel>
                      </Radio>
                      <OptionDesc level={2}>
                        {t('GEOFENCING_BLOCK_DESC')}
                      </OptionDesc>
                    </RadioWrapper>
                    <NestedBox>
                      <Text level={2}>{t('GEOFENCING_BLOCK_RECOMMENDED')}</Text>
                    </NestedBox>
                  </OptionCard>
                </OptionsRow>
              </Radio.Group>

              {(mode === GeofencingMode.WarningsOnly ||
                mode === GeofencingMode.CompleteBlock) && (
                <DistanceSection>
                  <Divider style={{ margin: 0 }} />
                  <DistanceLabelRow>
                    <SectionTitle>
                      {t('GEOFENCING_MAX_DISTANCE_FROM_VISIT')}
                    </SectionTitle>
                    <QuestionCircleOutlined
                      style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}
                    />
                  </DistanceLabelRow>
                  <div>
                    <DistanceInputRow>
                      <InputNumber
                        value={maxDistanceMeters}
                        onChange={(v) =>
                          setMaxDistanceMeters(v != null ? v : 0)
                        }
                        addonAfter={t('GEOFENCING_METRES')}
                        style={{ width: 342 }}
                        status={distanceError ? 'error' : undefined}
                      />
                    </DistanceInputRow>
                    {distanceError && (
                      <DistanceErrorText>
                        {t('GEOFENCING_MAX_DISTANCE_MIN_ERROR', {
                          min: MIN_DISTANCE_METRES
                        })}
                      </DistanceErrorText>
                    )}
                  </div>
                  {mode === GeofencingMode.WarningsOnly ? (
                    <NestedBox $compact>
                      <Text level={2}>{t('GEOFENCING_MAX_DISTANCE_NOTE')}</Text>
                    </NestedBox>
                  ) : (
                    <CompleteBlockWarningBox>
                      <CompleteBlockWarningTitle>
                        {t('GEOFENCING_BLOCK_IMPORTANT_TITLE')}
                      </CompleteBlockWarningTitle>
                      <CompleteBlockWarningMessage>
                        {t('GEOFENCING_BLOCK_IMPORTANT_MESSAGE')}
                      </CompleteBlockWarningMessage>
                    </CompleteBlockWarningBox>
                  )}
                </DistanceSection>
              )}
            </ContentCard>
            <FixedFooter>
              <Button
                variant="primary"
                disabled={!isDirty || distanceError}
                onClick={handleSave}
                isLoading={isUpdating}
              >
                <CheckOutlined />
                {t('SAVE_CHANGES')}
              </Button>
            </FixedFooter>
          </>
        )}
      </PageWrapper>

      <EnableGpsModal
        open={enableGpsModalOpen}
        onCancel={() => setEnableGpsModalOpen(false)}
        onConfirm={handleEnableGpsConfirm}
        isLoading={isEnablingGps}
      />
    </>
  )
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 60px;
`

const BlockContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
`

const LockIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`

const MessageText = styled(Text)`
  color: ${({ theme }) => theme.color?.neutral?.color4};
`

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
`

const ReadMoreLink = styled.a`
  color: #096dd9;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const ContentCard = styled(Card)`
  width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SectionTitle = styled(Text)`
  color: ${({ theme }) => theme.color?.neutral?.color4};
`

const OptionsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`

const OptionCard = styled(Card)<{ $selected: boolean }>`
  display: flex;
  padding: 8px 12px 12px 12px;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;
  border-radius: 8px;
  border: 1px solid
    ${({ $selected, theme }) =>
      $selected ? '#1890ff' : theme.color?.neutral?.color7};

  .ant-radio-wrapper {
    margin: 0;
    padding: 0;
  }
`

const OptionLabel = styled(Text)``

const OptionDesc = styled(Text)`
  display: block;
  color: ${({ theme }) => theme.color?.neutral?.color4};
  margin-left: 24px;
`

const NestedBox = styled.div<{ $compact?: boolean }>`
  margin-left: ${({ $compact }) => ($compact ? 0 : '24px')};
  width: ${({ $compact }) => ($compact ? 'fit-content' : 'auto')};
  padding: 8px 16px;
  color: ${({ theme }) => theme.color?.neutral?.color5};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color?.neutral?.color7};
  background: ${({ theme }) => theme.color?.neutral?.color10};
`

const CompleteBlockWarningBox = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #fa8c16;
  background: #fff7e6;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CompleteBlockWarningTitle = styled(Text)`
  color: ${({ theme }) => theme.color?.neutral?.color1};
  font-size: 18px;
  font-weight: 700;
`
const CompleteBlockWarningMessage = styled(Text)`
  color: ${({ theme }) => theme.color?.neutral?.color1};
  font-weight: 400;
`

const DistanceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const DistanceLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const DistanceInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const DistanceErrorText = styled.span`
  font-size: 14px;
  color: #ff4d4f;
`

const OptionNote = styled(Text)`
  display: block;
  margin-top: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.color?.neutral?.color5};
`

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FixedFooter = styled.div.attrs({
  className: 'lmc-fixed-footer'
})`
  border: none;
  display: flex;
  justify-content: flex-end;
  width: calc(100vw - 290px);
  @media (max-width: 768px) {
    width: 100vw;
  }
`

export default GeofencingSettingsScreen
